import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/** Serialises the Clinic Reports filters into a query string. */
function reportQuery({ from, to, appointmentType, providerId } = {}) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (appointmentType) params.set('appointmentType', appointmentType);
  if (providerId) params.set('providerId', providerId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Serialises the Message History Review filters into a query string. */
function messageLogQuery({ patient, sender, from, to, q } = {}) {
  const params = new URLSearchParams();
  if (patient) params.set('patient', patient);
  if (sender) params.set('sender', sender);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (q) params.set('q', q);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Scopes an onboarding call to one patient profile (ticket 7976). */
function patientQuery(patientId) {
  return patientId ? `?patientId=${encodeURIComponent(patientId)}` : '';
}

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  /**
   * Which patient the clinical tool on screen is open on (a patient profile
   * id), or null when none is. Sent with every request while set, so a row
   * acted on by its id — a task, a cycle, an assessment — is refused if it
   * belongs to a different patient from the one the clinician is looking at.
   */
  setPatientOnScreen(patientId) {
    this.patientOnScreen = patientId || null;
  }

  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // The patient a clinical tool is open on. The server refuses a write
        // to a row that is somebody else's (see setPatientOnScreen).
        ...(this.patientOnScreen ? { 'X-Koral-Patient': this.patientOnScreen } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      /**
       * Say which field, not just that something was wrong.
       *
       * The server already answers a rejected write with field-level detail —
       * `{ message: 'Validation failed', details: [{ path: 'description',
       * message: 'Required' }] }` — and this threw all of it away, so every
       * form in the product reported "Validation failed" and left the user to
       * guess which of a dozen inputs it meant. Found on the catalogue form,
       * where Description is required by the server, unmarked on the form, and
       * the failure named nothing.
       *
       * `details` is still attached for callers that want to mark the fields
       * themselves; this only makes the default message useful.
       */
      const details = payload?.error?.details;
      const base = payload?.error?.message || `Request failed (${res.status})`;
      const named = Array.isArray(details)
        ? details
            .map((d) => (d?.path ? `${d.path}: ${d.message || 'invalid'}` : d?.message))
            .filter(Boolean)
        : [];
      const error = new Error(named.length ? `${base} — ${named.join('; ')}` : base);
      error.status = res.status;
      error.details = details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  /**
   * Same auth/error handling as `request`, but returns the raw Blob — for file
   * downloads (CSV exports) where there is no `{ data }` envelope to unwrap.
   */
  async requestBlob(endpoint, { method = 'GET', headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    });

    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const payload = await res.json();
        message = payload?.error?.message || message;
      } catch {
        /* non-JSON error body — keep the status message */
      }
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }

    return res.blob();
  }

  // ---- Auth ----
  register({ email, name, password, dob, firstName, lastName, sex }) {
    return this.request('/auth/register', {
      method: 'POST',
      body: { email, name, password, dob, firstName, lastName, sex },
    });
  }

  login({ email, password, portal }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password, portal } });
  }

  verifyMfa({ mfaToken, code }) {
    return this.request('/auth/mfa/verify', { method: 'POST', body: { mfaToken, code } });
  }

  me() {
    return this.request('/auth/me');
  }

  forgotPassword({ email }) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  resetPassword({ token, password }) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password } });
  }

  // ---- Users (Admin account management) ----
  listUsers({ q, role, status, accountType, limit = 100, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (role) params.set('role', role);
    if (status) params.set('status', status);
    // Issue 8225: patient and internal staff accounts are listed separately.
    if (accountType) params.set('accountType', accountType);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this.request(`/users?${params.toString()}`);
  }

  createStaffUser(payload) {
    return this.request('/users', { method: 'POST', body: payload });
  }

  updateUserAccount(id, payload) {
    return this.request(`/users/${id}`, { method: 'PATCH', body: payload });
  }

  /**
   * Issue 8225: sends the account its own password-reset link. Admins never
   * set, see or hand over another user's password.
   */
  sendUserPasswordReset(id) {
    return this.request(`/users/${id}/send-password-reset`, { method: 'POST', body: {} });
  }

  // ---- Payment Gateway Configuration & Policy Engine (issue 8335) ----
  getPaymentGatewaySettings() {
    return this.request('/payment-gateway');
  }

  /** Each tab saves on its own, so each has its own call. */
  savePaymentGatewayConfig(payload) {
    return this.request('/payment-gateway/gateway', { method: 'PUT', body: payload });
  }

  savePaymentRefundPolicy(payload) {
    return this.request('/payment-gateway/policy', { method: 'PUT', body: payload });
  }

  savePaymentGatewayApi(payload) {
    return this.request('/payment-gateway/api', { method: 'PUT', body: payload });
  }

  testPaymentGatewayConnection() {
    return this.request('/payment-gateway/test-connection', { method: 'POST', body: {} });
  }

  getPaymentGatewayHistory() {
    return this.request('/payment-gateway/history');
  }

  /**
   * Issue 8312: what this patient still has to provide before a consultation
   * appointment can be booked.
   */
  /** Ticket 8529: the X-ray request form (PDF) for this patient. */
  downloadXrayReferral({ patientId } = {}) {
    const qs = patientId ? `?patientId=${encodeURIComponent(patientId)}` : '';
    return this.requestBlob(`/consultations/xray-referral${qs}`);
  }

  /** Ticket 8988: the generic X-ray Referral Letter (PDF), unticked and unsigned. */
  downloadXrayReferralLetter({ patientId } = {}) {
    const qs = patientId ? `?patientId=${encodeURIComponent(patientId)}` : '';
    return this.requestBlob(`/consultations/xray-referral/letter${qs}`);
  }

  getConsultationReadiness({ patientId } = {}) {
    const params = new URLSearchParams();
    if (patientId) params.set('patientId', patientId);
    const qs = params.toString();
    return this.request(`/consultations/readiness${qs ? `?${qs}` : ''}`);
  }

  /**
   * Issue 8292: the Smile Review interval in force for a patient — their own if
   * a clinician set one, otherwise their practice's default. Patients may read
   * their own; clinicians and admins may read anyone's and set it.
   */
  getReviewInterval({ userId, patientId } = {}) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (patientId) params.set('patientId', patientId);
    const qs = params.toString();
    return this.request(`/clinic-branches/review-interval${qs ? `?${qs}` : ''}`);
  }

  /** `interval: null` puts the patient back on their practice's default. */
  setReviewInterval({ userId, patientId, interval }) {
    return this.request('/clinic-branches/review-interval', {
      method: 'PUT',
      body: { userId, patientId: patientId || null, interval: interval || null },
    });
  }

  /**
   * Issue 8231: a patient account with its family and KORAL journey context.
   * Read-only — journey status is system-derived, and account changes still go
   * through updateUserAccount.
   */
  getPatientAccount(id) {
    return this.request(`/users/${id}/patient-account`);
  }

  /**
   * Issue 8226: the User Management audit log. Read-only — the client can page
   * and filter it, and nothing more.
   */
  listAccountAudit({ q, accountType, actorUserId, action, from, to, limit = 25, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (accountType) params.set('accountType', accountType);
    if (actorUserId) params.set('actorUserId', actorUserId);
    if (action) params.set('action', action);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this.request(`/account-audit?${params.toString()}`);
  }

  // ---- Practice Management (clinic branches, issue 8221) ----
  listClinicBranches({ includeInactive = false } = {}) {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    const qs = params.toString();
    return this.request(`/clinic-branches${qs ? `?${qs}` : ''}`);
  }

  createClinicBranch(payload) {
    return this.request('/clinic-branches', { method: 'POST', body: payload });
  }

  updateClinicBranch(id, payload) {
    return this.request(`/clinic-branches/${id}`, { method: 'PUT', body: payload });
  }

  listClinicBranchUsers(id) {
    return this.request(`/clinic-branches/${id}/users`);
  }

  assignClinicBranchUsers(id, userIds) {
    return this.request(`/clinic-branches/${id}/users`, { method: 'POST', body: { userIds } });
  }

  unassignClinicBranchUser(id, userId) {
    return this.request(`/clinic-branches/${id}/users/${userId}`, { method: 'DELETE' });
  }

  // ---- Roles & permissions ----
  listRoles() {
    return this.request('/roles');
  }

  createRole(payload) {
    return this.request('/roles', { method: 'POST', body: payload });
  }

  updateRole(name, payload) {
    return this.request(`/roles/${encodeURIComponent(name)}`, { method: 'PUT', body: payload });
  }

  /** Delete a custom role (issue 8274). Refused for system or assigned roles. */
  deleteRole(name) {
    return this.request(`/roles/${encodeURIComponent(name)}`, { method: 'DELETE' });
  }

  getMyPermissions() {
    return this.request('/roles/permissions/me');
  }

  // ---- Product & Service Catalogue (Admin) ----
  listCatalogue({ q, type, status, page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (type) params.set('type', type);
    if (status) params.set('status', status);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return this.request(`/catalogue?${params.toString()}`);
  }

  getCatalogueItem(id) {
    return this.request(`/catalogue/${id}`);
  }

  createCatalogueItem(payload) {
    return this.request('/catalogue', { method: 'POST', body: payload });
  }

  updateCatalogueItem(id, payload) {
    return this.request(`/catalogue/${id}`, { method: 'PUT', body: payload });
  }

  deleteCatalogueItem(id) {
    return this.request(`/catalogue/${id}`, { method: 'DELETE' });
  }

  // ---- KORAL Treatment Packages (Admin, reference data) ----
  listTreatmentPackages({
    q,
    category,
    phase,
    status,
    packageType,
    treatmentType,
    sort,
    direction,
    page = 1,
    pageSize = 10,
  } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (category) params.set('category', category);
    if (phase) params.set('phase', phase);
    if (status) params.set('status', status);
    if (packageType) params.set('packageType', packageType);
    // Issue 8311: filter by Treatment Library item, and sort on the structured
    // values rather than on the formatted cost or length text.
    if (treatmentType) params.set('treatmentType', treatmentType);
    if (sort) params.set('sort', sort);
    if (direction) params.set('direction', direction);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return this.request(`/treatment-packages?${params.toString()}`);
  }

  /** Issue 8310: the package's own change history, newest first. */
  getTreatmentPackageHistory(id) {
    return this.request(`/treatment-packages/${id}/history`);
  }

  // ---- Treatment Library (issue 8310) ----
  /** Standardised treatment types; the source for Treatment Type(s). */
  listTreatmentLibrary({ q, category, status } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    const qs = params.toString();
    return this.request(`/treatment-library${qs ? `?${qs}` : ''}`);
  }

  /**
   * The configurable taxonomies a package uses — Complexity / Mechanics,
   * Extraction, length units and education resource types. Served from data so
   * the temporary values can be replaced without a release.
   */
  getTreatmentTaxonomies() {
    return this.request('/treatment-library/taxonomies');
  }

  getTreatmentPackage(id) {
    return this.request(`/treatment-packages/${id}`);
  }

  createTreatmentPackage(payload) {
    return this.request('/treatment-packages', { method: 'POST', body: payload });
  }

  updateTreatmentPackage(id, payload) {
    return this.request(`/treatment-packages/${id}`, { method: 'PUT', body: payload });
  }

  deleteTreatmentPackage(id) {
    return this.request(`/treatment-packages/${id}`, { method: 'DELETE' });
  }

  // ---- Notification inbox (any authenticated role — navbar bell) ----
  getMyNotifications() {
    return this.request('/notifications/mine');
  }

  markMyNotificationsRead() {
    return this.request('/notifications/mine/read', { method: 'POST', body: {} });
  }

  // ---- 8694 — Notification Center ----
  getNotificationCenter() {
    return this.request('/notifications/center');
  }

  markNotificationRead(id) {
    return this.request(`/notifications/center/${id}/read`, { method: 'POST', body: {} });
  }

  getNotificationPreferences() {
    return this.request('/notifications/preferences');
  }

  saveNotificationPreferences(channels) {
    return this.request('/notifications/preferences', { method: 'PUT', body: { channels } });
  }

  getNotificationDeliveryLog(filters = {}) {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return this.request(`/notifications/delivery-log${q ? `?${q}` : ''}`);
  }

  // ---- AI narrative (LLM) settings (Admin — keys write-only, masked reads) ----
  getLlmSettings() {
    return this.request('/clinic-settings/llm');
  }

  updateLlmSettings(payload) {
    return this.request('/clinic-settings/llm', { method: 'PUT', body: payload });
  }

  testLlmSettings() {
    return this.request('/clinic-settings/llm/test', { method: 'POST', body: {} });
  }

  // ---- Clinical Engine (Dentist/Admin — draft plans, never patient-facing) ----
  getEngineDraft(caseId) {
    return this.request(`/clinical-engine/draft/${encodeURIComponent(caseId)}`);
  }

  evaluateEnginePlan(payload) {
    return this.request('/clinical-engine/evaluate', { method: 'POST', body: payload });
  }

  sendVisionFeedback(payload) {
    return this.request('/clinical-engine/vision-feedback', { method: 'POST', body: payload });
  }

  /** AI Training Readiness (admin) — what the corpus actually holds. */
  getTrainingReadiness() {
    return this.request('/clinical-engine/training-readiness');
  }

  /* Realignment 4.5 — the troubleshooting library. */
  listTroubleshootingArticles() {
    return this.request('/troubleshooting/articles');
  }

  createTroubleshootingArticle(article) {
    return this.request('/troubleshooting/articles', { method: 'POST', body: article });
  }

  updateTroubleshootingArticle(id, patch) {
    return this.request(`/troubleshooting/articles/${encodeURIComponent(id)}`, { method: 'PUT', body: patch });
  }

  approveTroubleshootingArticle(id) {
    return this.request(`/troubleshooting/articles/${encodeURIComponent(id)}/approve`, { method: 'POST', body: {} });
  }

  retireTroubleshootingArticle(id, reason) {
    return this.request(`/troubleshooting/articles/${encodeURIComponent(id)}/retire`, { method: 'POST', body: { reason } });
  }

  /** Approved articles only, optionally narrowed by tags (appliance types / issue category). */
  getApprovedTroubleshooting(tags = []) {
    const q = tags.length ? `?tags=${encodeURIComponent(tags.join(','))}` : '';
    return this.request(`/troubleshooting/approved${q}`);
  }

  getVisionSkill() {
    return this.request('/clinical-engine/vision-skill');
  }

  regenerateVisionSkill() {
    return this.request('/clinical-engine/vision-skill/regenerate', { method: 'POST', body: {} });
  }

  activateVisionSkill(version) {
    return this.request(`/clinical-engine/vision-skill/${version}/activate`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Package Coverage Analysis for the Treatment Packages master screen (Admin).
   * `briefing: false` returns the counted analysis alone, skipping the LLM call.
   */
  getPackageCoverage({ offersTmj = false, offersAirway = false, briefing = true } = {}) {
    const q = new URLSearchParams({
      offersTmj: String(offersTmj),
      offersAirway: String(offersAirway),
      briefing: String(briefing),
    });
    return this.request(`/clinical-engine/package-coverage?${q.toString()}`);
  }

  getVisionPatients() {
    return this.request('/clinical-engine/vision-patients');
  }

  /** 8793 — Run AI analysis on a plan: the engine's findings join it as proposals. */
  runPlanAiAnalysis(planId) {
    return this.request(`/treatment-plan/${planId}/ai-analysis`, { method: 'POST' });
  }

  analyzePatientPhotos(patientId) {
    return this.request('/clinical-engine/vision-draft', { method: 'POST', body: { patientId } });
  }

  /**
   * ADA item reference for claim assembly (library v2.1, DB6). No fees — the
   * practice keeps its own fee table keyed by ADA item.
   */
  getAdaItems(q) {
    return this.request(`/billing/ada-items${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  }

  /**
   * My Smile Review (patient) — the patient's own side of the loop.
   *
   * Scoped server-side to the caller's token, so there is no user id to pass.
   * Having no submissions is a normal answer here, not an error.
   */
  getMySmileReview(patientId) {
    return this.request(`/smile-reviews/mine${patientQuery(patientId)}`);
  }

  /**
   * Remote Monitoring (patient) — the cycle, the five-view Monitoring Scan and
   * the scan history. Scoped server-side to the caller's token.
   */
  getMyMonitoring(patientId) {
    return this.request(`/monitoring/me${patientQuery(patientId)}`);
  }

  startMonitoringScan(patientId) {
    return this.request(`/monitoring/me/scans${patientQuery(patientId)}`, { method: 'POST' });
  }

  /**
   * 8652 — the Retention Dashboard (Step 9) and its retainer scan, which reuses
   * the established five-view guided capture.
   */
  getRetention(patientId) {
    return this.request(`/monitoring/me/retention${patientQuery(patientId)}`);
  }

  async uploadRetainerView({ slot, file, patientId }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const body = new FormData();
    body.append('file', file);
    const res = await fetch(`${API_BASE_URL}/monitoring/me/retention/views/${slot}${patientQuery(patientId)}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || 'This photo could not be accepted.');
      err.status = res.status;
      throw err;
    }
    return payload?.data;
  }

  submitRetainerScan(patientId) {
    return this.request(`/monitoring/me/retention/submit${patientQuery(patientId)}`, { method: 'POST' });
  }

  /** 8729 — the Growth & Development Program, for the parent or account holder. */
  getGrowth(patientId) {
    return this.request(`/monitoring/me/growth${patientQuery(patientId)}`);
  }

  async uploadGrowthView({ slot, file, patientId }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const body = new FormData();
    body.append('file', file);
    const res = await fetch(`${API_BASE_URL}/monitoring/me/growth/views/${slot}${patientQuery(patientId)}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || 'This photo could not be accepted.');
      err.status = res.status;
      throw err;
    }
    return payload?.data;
  }

  submitGrowthAssessment(patientId) {
    return this.request(`/monitoring/me/growth/submit${patientQuery(patientId)}`, { method: 'POST' });
  }

  /** 8643 — Treatment Completion (Step 8): the completion record, and the move to Retention (Step 9). */
  getTreatmentCompletion(patientId) {
    return this.request(`/monitoring/me/completion${patientQuery(patientId)}`);
  }

  continueToRetention(patientId) {
    return this.request(`/monitoring/me/retention${patientQuery(patientId)}`, { method: 'POST' });
  }

  /** Add or retake one of the five required views. */
  async uploadMonitoringView({ scanId, slot, file, patientId }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(
      `${API_BASE_URL}/monitoring/me/scans/${scanId}/views/${slot}${patientQuery(patientId)}`,
      {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: form,
      }
    );
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || `That photo could not be sent (${res.status})`);
      err.status = res.status;
      throw err;
    }
    // Realignment 4.2 — the upload checks ride back with the scan.
    return { ...payload?.data?.scan, uploadCheck: payload?.data?.uploadCheck || null };
  }

  /**
   * Realignment 4.1 — add (or replace) the scan's one optional video.
   *
   * XMLHttpRequest rather than fetch, because fetch reports no upload
   * progress and a 30-second phone video can take a while on mobile data.
   * `onProgress` receives 0-100.
   */
  async uploadMonitoringVideo({ scanId, file, patientId, onProgress }) {
    const data = await this.#sendVideo(`/monitoring/me/scans/${scanId}/video${patientQuery(patientId)}`, file, onProgress);
    return data?.scan;
  }

  /**
   * Realignment 4.1 follow-up — the optional video of a retainer scan or a
   * G&D assessment still being taken. Neither has a scan row until it is sent,
   * so the video belongs to the programme's draft; the answer is the page's
   * data, as for a photograph of those flows.
   */
  uploadRetentionVideo({ file, patientId, onProgress }) {
    return this.#sendVideo(`/monitoring/me/retention/video${patientQuery(patientId)}`, file, onProgress);
  }

  removeRetentionVideo(patientId) {
    return this.request(`/monitoring/me/retention/video${patientQuery(patientId)}`, { method: 'DELETE' });
  }

  uploadGrowthVideo({ file, patientId, onProgress }) {
    return this.#sendVideo(`/monitoring/me/growth/video${patientQuery(patientId)}`, file, onProgress);
  }

  removeGrowthVideo(patientId) {
    return this.request(`/monitoring/me/growth/video${patientQuery(patientId)}`, { method: 'DELETE' });
  }

  /** One video upload with progress; resolves with the response's `data`. */
  #sendVideo(path, file, onProgress) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const form = new FormData();
    form.append('file', file);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}${path}`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        let payload = null;
        try {
          payload = JSON.parse(xhr.responseText);
        } catch {
          payload = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(payload?.data);
        const err = new Error(payload?.error?.message || `That video could not be sent (${xhr.status})`);
        err.status = xhr.status;
        return reject(err);
      };
      xhr.onerror = () => reject(new Error('That video could not be sent. Check your connection and try again.'));
      xhr.send(form);
    });
  }

  removeMonitoringVideo(scanId, patientId) {
    return this.request(`/monitoring/me/scans/${scanId}/video${patientQuery(patientId)}`, { method: 'DELETE' });
  }

  /**
   * Realignment 4.1 — a scan's video as an object URL, for its patient or a
   * clinician. The bearer token cannot ride on a <video src>, so the bytes are
   * fetched and played from a blob: URL (the prod CSP's media-src allows
   * blob:, as for the education videos of 8487). A blob plays and seeks
   * locally; the route's Range support serves any direct reader.
   */
  async fetchMonitoringVideoUrl(scanId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/monitoring/scans/${scanId}/video`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.ok) return { url: URL.createObjectURL(await res.blob()), reason: '' };
    const reason = await res
      .json()
      .then((body) => body?.error?.message || '')
      .catch(() => '');
    return { url: null, reason: reason || 'This video could not be opened.' };
  }

  submitMonitoringScan(scanId, patientId) {
    return this.request(`/monitoring/me/scans/${scanId}/submit${patientQuery(patientId)}`, {
      method: 'POST',
    });
  }

  /* ---- 8693 — Request Extra Scan (clinician) ---- */
  getExtraScanTypes() {
    return this.request('/monitoring/extra-scan/types');
  }

  listExtraScans(query) {
    return this.request(`/monitoring/extra-scan${this.#smileReviewQuery(query)}`);
  }

  /** Send Request: `{ views: [{slot, instruction} | {custom: true, label, instruction}], reason, guidance }`. */
  requestExtraScan(query, payload) {
    return this.request(`/monitoring/extra-scan${this.#smileReviewQuery(query)}`, {
      method: 'POST',
      body: payload,
    });
  }

  getExtraScan(requestId) {
    return this.request(`/monitoring/extra-scan/${requestId}`);
  }

  updateExtraScan(requestId, { instructions, guidance }) {
    return this.request(`/monitoring/extra-scan/${requestId}`, { method: 'PUT', body: { instructions, guidance } });
  }

  withdrawExtraScan(requestId, reason) {
    return this.request(`/monitoring/extra-scan/${requestId}/withdraw`, { method: 'POST', body: { reason } });
  }

  /* ---- 8693 — Request Extra Scan (patient) ---- */
  complyExtraScan(requestId, patientId) {
    return this.request(`/monitoring/me/extra-scan/${requestId}/comply${patientQuery(patientId)}`, { method: 'POST' });
  }

  async uploadExtraScanView({ requestId, slot, file, patientId }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(
      `${API_BASE_URL}/monitoring/me/extra-scan/${requestId}/views/${slot}${patientQuery(patientId)}`,
      { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form }
    );
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || `That photo could not be sent (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return payload?.data;
  }

  submitExtraScan(requestId, patientId) {
    return this.request(`/monitoring/me/extra-scan/${requestId}/submit${patientQuery(patientId)}`, { method: 'POST' });
  }

  /* ---- 8696 — Appointment Invitation ---- */
  getBookPatientContext(query, origin = null) {
    // 8916: from Patient Manager the server judges eligibility by its own gate.
    const qs = this.#smileReviewQuery(query);
    const tail = origin ? `${qs ? '&' : '?'}origin=${encodeURIComponent(origin)}` : '';
    return this.request(`/appointment-invitations/context${qs}${tail}`);
  }

  sendAppointmentInvitation(query, payload) {
    return this.request(`/appointment-invitations${this.#smileReviewQuery(query)}`, { method: 'POST', body: payload });
  }

  withdrawAppointmentInvitation(id) {
    return this.request(`/appointment-invitations/${id}/withdraw`, { method: 'POST' });
  }

  /** 8871 / 8875: the signed-in patient's appointments and pending invitations. */
  getMyAppointments(patientId) {
    return this.request(`/appointments/mine${patientQuery(patientId)}`);
  }

  getMyAppointmentInvitations(patientId) {
    return this.request(`/appointment-invitations/mine${patientQuery(patientId)}`);
  }

  selectAppointmentSlot(invitationId, slotId, patientId) {
    return this.request(`/appointment-invitations/mine/${invitationId}/select${patientQuery(patientId)}`, {
      method: 'POST',
      body: { slotId },
    });
  }

  /* ---- 8689 — Clinical Image Annotation & Feedback ---- */
  getScanAnnotations(scanId) {
    return this.request(`/clinical-annotations/scans/${scanId}`);
  }

  getImageAnnotation(scanId, slot) {
    return this.request(`/clinical-annotations/scans/${scanId}/views/${encodeURIComponent(slot)}`);
  }

  saveImageAnnotation(scanId, slot, { items, feedback, version }) {
    return this.request(`/clinical-annotations/scans/${scanId}/views/${encodeURIComponent(slot)}`, {
      method: 'PUT',
      body: { items, feedback, version },
    });
  }

  /** 8627: start Remote Monitoring for the patient in the workspace. */
  activateMonitoring(query, { intervalDays, intervalLabel } = {}) {
    return this.request(`/monitoring/activate${this.#smileReviewQuery(query)}`, {
      method: 'POST',
      body: { intervalDays, intervalLabel },
    });
  }

  /** 8621: the Smile Review Plan's lifecycle controls. */
  /**
   * 8644 — Save from the Interval Control: the configuration, and the
   * activation or Treatment End the clinician explicitly ticked.
   */
  saveMonitoringPlan(query, { intervalDays, intervalLabel, activate, hold, resume, treatmentEnd, outcome, reason } = {}) {
    return this.request(`/monitoring/plan${this.#smileReviewQuery(query)}`, {
      method: 'PUT',
      // Realignment 4.6b: `outcome` is required with Treatment End.
      body: { intervalDays, intervalLabel, activate, hold, resume, treatmentEnd, outcome, reason },
    });
  }

  /** Realignment 4.6b — the Treatment End outcome form's objectives, and any outcome already recorded. */
  getTreatmentOutcomeContext(query) {
    return this.request(`/monitoring/outcome-context${this.#smileReviewQuery(query)}`);
  }

  /** 8699 — Save on the Retention Monitoring Plan (Clinical Smile Review, Retention Mode). */
  saveRetentionPlan(query, plan = {}) {
    return this.request(`/monitoring/retention-plan${this.#smileReviewQuery(query)}`, { method: 'PUT', body: plan });
  }

  /** 8699 — ask the patient for a new retention scan now. */
  requestRetentionRescan(query, reason) {
    return this.request(`/monitoring/retention-plan/rescan${this.#smileReviewQuery(query)}`, {
      method: 'POST',
      body: { reason },
    });
  }

  /** 8729 — place the patient in the G&D Program (Clinical Smile Review). */
  startGrowthProgram(query, reason) {
    return this.request(`/monitoring/growth/start${this.#smileReviewQuery(query)}`, { method: 'POST', body: { reason } });
  }

  /** 8729 — Save on the G&D Monitoring Plan. */
  saveGrowthPlan(query, plan = {}) {
    return this.request(`/monitoring/growth-plan${this.#smileReviewQuery(query)}`, { method: 'PUT', body: plan });
  }

  /** 8729 — ask the parent for a new G&D assessment now. */
  requestGrowthResubmission(query, reason) {
    return this.request(`/monitoring/growth-plan/resubmission${this.#smileReviewQuery(query)}`, {
      method: 'POST',
      body: { reason },
    });
  }

  holdMonitoring(cycleId, reason) {
    return this.request(`/monitoring/cycles/${cycleId}/hold`, { method: 'POST', body: { reason } });
  }

  resumeMonitoring(cycleId, reason) {
    return this.request(`/monitoring/cycles/${cycleId}/resume`, { method: 'POST', body: { reason } });
  }

  updateMonitoringPlan(cycleId, { intervalDays, intervalLabel, reason }) {
    return this.request(`/monitoring/cycles/${cycleId}/plan`, {
      method: 'PUT',
      body: { intervalDays, intervalLabel, reason },
    });
  }

  endMonitoringTreatment(cycleId, reason, outcome) {
    return this.request(`/monitoring/cycles/${cycleId}/complete`, { method: 'POST', body: { reason, outcome } });
  }

  startMonitoringRetention(cycleId, reason) {
    return this.request(`/monitoring/cycles/${cycleId}/retention`, { method: 'POST', body: { reason } });
  }

  // ---- Clinical Smile Review (Dentist/Admin — the clinician side of patient
  // photo monitoring; the patient's own view is My Smile Review above) ----
  getSmileReviewQueue() {
    return this.request('/smile-reviews');
  }

  getSmileReviewDetail(userId, patientId) {
    return this.request(`/smile-reviews/${encodeURIComponent(userId)}${patientQuery(patientId)}`);
  }

  submitSmileReview(userId, patientId, body) {
    return this.request(`/smile-reviews/${encodeURIComponent(userId)}${patientQuery(patientId)}`, {
      method: 'POST',
      body,
    });
  }

  /**
   * Submission photo bytes for a clinician. Its own endpoint, because the
   * patient media route is owner-only by design and should stay that way.
   *
   * Returns the reason alongside the url: a clinician looking at an empty frame
   * needs to know whether the patient sent nothing or the server lost the file.
   */
  async fetchSmileReviewPhotoUrl(mediaId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/smile-reviews/photo/${mediaId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.ok) return { url: URL.createObjectURL(await res.blob()), reason: '' };
    const reason = await res
      .json()
      .then((body) => body?.error?.message || '')
      .catch(() => '');
    return { url: null, reason: reason || 'This photo could not be opened.' };
  }

  // ---- Case Decision Viewer (Dentist/Admin, Build V2) ----
  getCaseDecisionQueue() {
    return this.request('/case-decisions');
  }

  getCaseDecision(caseId) {
    return this.request(`/case-decisions/${encodeURIComponent(caseId)}`);
  }

  /** A tidy-up suggestion for dictated text. Never sent on the clinician's behalf. */
  tidyCaseDictation(text, caseId) {
    return this.request('/case-decisions/tidy', { method: 'POST', body: { text, ...(caseId ? { caseId } : {}) } });
  }

  // ---- Realignment L6: optional data-use permission (patient) ----
  getDataUseConsents() {
    return this.request('/account/data-use-consent');
  }

  setDataUseConsent({ profileId, granted, wordingVersion, source }) {
    return this.request('/account/data-use-consent', {
      method: 'PUT',
      body: { profileId, granted, wordingVersion, source },
    });
  }

  /**
   * Realignment L6 (admin): the de-identified dataset of consented patients,
   * saved by the browser. Returns the export's counts from the response header.
   */
  async downloadDatasetExport({ includeDraftWording = false } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/clinical-engine/dataset-export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ includeDraftWording }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error?.message || `Export failed (${res.status})`);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || 'koral-dataset.zip';
    let counts = null;
    try {
      counts = JSON.parse(res.headers.get('X-Koral-Export') || 'null');
    } catch {
      counts = null;
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return { filename, counts };
  }

  /** Write the instruction back to the patient file and advance the queue. */
  recordCaseDecision(caseId, body) {
    return this.request(`/case-decisions/${encodeURIComponent(caseId)}`, { method: 'POST', body });
  }

  // ---- Appointment types (Admin settings) ----
  listAppointmentTypes({ includeArchived = false } = {}) {
    return this.request(`/appointment-types${includeArchived ? '?includeArchived=true' : ''}`);
  }

  createAppointmentType(payload) {
    return this.request('/appointment-types', { method: 'POST', body: payload });
  }

  updateAppointmentType(id, payload) {
    return this.request(`/appointment-types/${id}`, { method: 'PUT', body: payload });
  }

  // ---- Conversation Inbox (staff) ----
  listConversations({ status, type, assignedTo, patientName } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (type) params.set('type', type);
    if (assignedTo) params.set('assignedTo', assignedTo);
    if (patientName) params.set('patientName', patientName);
    const qs = params.toString();
    return this.request(`/conversations${qs ? `?${qs}` : ''}`);
  }

  getConversationAssignees() {
    return this.request('/conversations/assignees');
  }

  getConversation(id) {
    return this.request(`/conversations/${id}`);
  }

  replyToConversation(id, body) {
    return this.request(`/conversations/${id}/messages`, { method: 'POST', body: { body } });
  }

  updateConversation(id, payload) {
    return this.request(`/conversations/${id}`, { method: 'PUT', body: payload });
  }

  /** Link a conversation that belongs to nobody to a patient from the patient list (refused if they already have one). */
  linkConversationToPatient(id, patientId) {
    return this.request(`/conversations/${id}/patient`, { method: 'PUT', body: { patientId } });
  }

  // ---- Message History Review (admin compliance log) ----
  listMessageLogs(filters = {}) {
    return this.request(`/message-logs${messageLogQuery(filters)}`);
  }

  getMessageLogPatients() {
    return this.request('/message-logs/patients');
  }

  /** Escalation monitor: escalated conversations with reason, owner, duration. */
  listEscalations({ status, assignedTo } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (assignedTo) params.set('assignedTo', assignedTo);
    const qs = params.toString();
    return this.request(`/message-logs/escalations${qs ? `?${qs}` : ''}`);
  }

  getMessageLogThread(conversationId) {
    return this.request(`/message-logs/threads/${conversationId}`);
  }

  /** Downloads the filtered compliance log as a CSV blob (not the JSON envelope). */
  exportMessageLogs(filters = {}) {
    return this.requestBlob(`/message-logs/export${messageLogQuery(filters)}`);
  }

  // ---- Patient Lookup (front desk) ----
  searchPatients({ name, phone, dob } = {}) {
    const params = new URLSearchParams();
    if (name) params.set('name', name);
    if (phone) params.set('phone', phone);
    if (dob) params.set('dob', dob);
    const qs = params.toString();
    return this.request(`/patients${qs ? `?${qs}` : ''}`);
  }

  getPatientProfile(id) {
    return this.request(`/patients/${id}`);
  }

  /** 8716: the case reference a patient's Clinical Tools open (null when they have no case). */
  getPatientCaseRef(id) {
    return this.request(`/patients/${id}/case`);
  }

  // ---- Billing Management (front desk) ----
  getBillingSummary() {
    return this.request('/billing/summary');
  }

  listAppointmentsPendingInvoice({ patient } = {}) {
    const qs = patient ? `?patient=${encodeURIComponent(patient)}` : '';
    return this.request(`/billing/pending-appointments${qs}`);
  }

  listInvoices({ status, patient } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (patient) params.set('patient', patient);
    const qs = params.toString();
    return this.request(`/billing/invoices${qs ? `?${qs}` : ''}`);
  }

  createInvoice(payload) {
    return this.request('/billing/invoices', { method: 'POST', body: payload });
  }

  getInvoice(id) {
    return this.request(`/billing/invoices/${id}`);
  }

  issueInvoice(id) {
    return this.request(`/billing/invoices/${id}/issue`, { method: 'POST' });
  }

  recordInvoicePayment(id, payload) {
    return this.request(`/billing/invoices/${id}/payments`, { method: 'POST', body: payload });
  }

  // ---- Clinic Reports (practitioner) ----
  getPracticeReport(filters = {}) {
    return this.request(`/reports/practice${reportQuery(filters)}`);
  }

  getReportAppointmentTypes(filters = {}) {
    return this.request(`/reports/appointment-types${reportQuery(filters)}`);
  }

  /** Clinic-wide report (admin). */
  getClinicReport(filters = {}) {
    return this.request(`/reports/clinic${reportQuery(filters)}`);
  }

  /** Invoiced revenue for each of the trailing six calendar months (admin). */
  getClinicRevenueTrend(filters = {}) {
    return this.request(`/reports/clinic/revenue-trend${reportQuery(filters)}`);
  }

  exportClinicReport(format, filters = {}) {
    return this.requestBlob(`/reports/clinic/export.${format}${reportQuery(filters)}`);
  }

  exportPracticeReport(format, filters = {}) {
    return this.requestBlob(`/reports/practice/export.${format}${reportQuery(filters)}`);
  }

  // ---- Manual Notification Composer ----
  getNotificationRecipients() {
    return this.request('/notifications/recipients');
  }

  getNotificationSegments() {
    return this.request('/notifications/segments');
  }

  listSentNotifications() {
    return this.request('/notifications');
  }

  sendNotification(payload) {
    return this.request('/notifications', { method: 'POST', body: payload });
  }

  // ---- Clinic settings & branding ----
  getClinicSettings() {
    return this.request('/clinic-settings');
  }

  updateClinicSettings(payload) {
    return this.request('/clinic-settings', { method: 'PUT', body: payload });
  }

  // ---- AI transparency: the run behind a provenance pill (staff only) ----
  getAiRun(id) {
    return this.request(`/clinical-engine/runs/${encodeURIComponent(id)}`);
  }

  getAiRunFinding(id, code) {
    return this.request(`/clinical-engine/runs/${encodeURIComponent(id)}/finding/${encodeURIComponent(code)}`);
  }

  /** The patient's most recent run of a kind, or `{ run: null }`. */
  getLatestAiRun(patient, kind = 'detection') {
    return this.request(`/clinical-engine/runs/latest?patient=${encodeURIComponent(patient)}&kind=${encodeURIComponent(kind)}`);
  }

  getVisionSkillVersion(version) {
    return this.request(`/clinical-engine/vision-skill/${encodeURIComponent(version)}`);
  }

  /** The clinical library's finding categories, for the patient-view setting. */
  getFindingCategories() {
    return this.request('/clinic-settings/finding-categories');
  }

  // ---- Security settings ----
  getSecuritySettings() {
    return this.request('/security-settings');
  }

  updateSecuritySettings(payload) {
    return this.request('/security-settings', { method: 'PUT', body: payload });
  }

  // ---- Patient dashboard ----
  getPatientDashboard() {
    return this.request('/patient-dashboard');
  }

  // ---- Onboarding ----
  // Every onboarding call is scoped to one patient (ticket 7976). `patientId`
  // is the profile the wizard is currently filling in; omitting it means the
  // account owner's own onboarding, which is what the backend falls back to.
  getOnboardingProfiles() {
    return this.request('/onboarding/profiles');
  }

  getOnboardingProfile(patientId) {
    return this.request(`/onboarding/profile${patientQuery(patientId)}`);
  }

  saveOnboardingProfile(profile, patientId) {
    return this.request(`/onboarding/profile${patientQuery(patientId)}`, {
      method: 'PUT',
      body: profile,
    });
  }

  getOnboardingStep(stepKey, patientId) {
    return this.request(`/onboarding/steps/${stepKey}${patientQuery(patientId)}`);
  }

  saveOnboardingStep(stepKey, { data, status }, patientId) {
    return this.request(`/onboarding/steps/${stepKey}${patientQuery(patientId)}`, {
      method: 'PUT',
      body: { data, status },
    });
  }

  listOnboardingMedia(patientId) {
    return this.request(`/onboarding/media${patientQuery(patientId)}`);
  }

  /**
   * Upload a media file (multipart). kind: photo | xray | stl.
   * Realignment 3.1: an X-ray may carry `xrayType` (opg | lateral_ceph | other);
   * left out when the patient is not sure.
   */
  async uploadOnboardingMedia({ kind, slot, file, patientId, xrayType }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const form = new FormData();
    form.append('kind', kind);
    if (slot) form.append('slot', slot);
    if (kind === 'xray' && xrayType) form.append('xrayType', xrayType);
    if (patientId) form.append('patientId', patientId);
    form.append('file', file);
    const res = await fetch(`${API_BASE_URL}/onboarding/media`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    // The slot check rides back with the media: the upload succeeded either
    // way, and the caller decides whether to say anything about it.
    // Realignment 1.2 — and so does the measured photo quality.
    return {
      ...payload?.data?.media,
      slotCheck: payload?.data?.slotCheck || null,
      quality: payload?.data?.quality || null,
    };
  }

  deleteOnboardingMedia(id) {
    return this.request(`/onboarding/media/${id}`, { method: 'DELETE' });
  }

  /** Ticket 9014: the patient says which X-ray an uploaded file is ('' = not sure). */
  setOnboardingXrayType(id, xrayType) {
    return this.request(`/onboarding/media/${encodeURIComponent(id)}/xray-type`, { method: 'PUT', body: { xrayType } });
  }

  /** Client request #1 — the patient's smile simulation state (the first read starts it). */
  getSmileSimulation(patientId) {
    return this.request(`/smile-simulation/mine${patientQuery(patientId)}`);
  }

  /** The generated "after" image, as an object URL. */
  async fetchSmileSimulationUrl(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/smile-simulation/${id}/image`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  }

  /** Fetch a protected media file as an object URL (auth header can't ride on <img>). */
  async fetchMediaObjectUrl(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/onboarding/media/${id}/content`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }

  getOnboardingSummary(patientId) {
    return this.request(`/onboarding/summary${patientQuery(patientId)}`);
  }

  submitOnboarding(patientId) {
    return this.request(`/onboarding/submit${patientQuery(patientId)}`, { method: 'POST' });
  }

  // ---- Assessments ----
  /**
   * Raise an assessment request for one patient.
   *
   * Ticket 8099: the parameters used to be flattened into a single `goals`
   * object, so a caller passing `{ goals, patientId }` had its `patientId`
   * nested inside `goals` where the API never looked — every request from the
   * onboarding review page fell back to the account owner, whoever had actually
   * been onboarded. `settleFreeFee` tells the API the fee was confirmed in-page
   * rather than at the gateway (it is $0 under the current promotion).
   */
  createAssessment({ goals, patientId, settleFreeFee = false } = {}) {
    return this.request(`/assessments${patientQuery(patientId)}`, {
      method: 'POST',
      body: { goals, patientId, settleFreeFee },
    });
  }

  getLatestAssessment(patientId) {
    return this.request(`/assessments/latest${patientQuery(patientId)}`);
  }

  /** Ticket 8100: record that the patient has opened their plan. */
  /**
   * The unified event taxonomy: the two events only the patient's app can see
   * (a plan view, an education asset opened). Fire-and-forget by design.
   */
  recordPatientEvent(type, { patientId, entityId, payload } = {}) {
    return this.request('/events/patient', {
      method: 'POST',
      body: { type, patientId: patientId || undefined, entityId, payload },
    }).catch(() => null);
  }

  markAssessmentViewed(assessmentId) {
    return this.request(`/assessments/${assessmentId}/viewed`, { method: 'POST' });
  }

  /** The itemised payable the checkout renders, with instalment options. */
  getPayable({ type, id, promoCode }) {
    const q = new URLSearchParams({ type, id });
    if (promoCode) q.set('promoCode', promoCode);
    return this.request(`/payments/payable?${q.toString()}`);
  }

  /** Validate a promo code and reprice the payable. */
  validatePromoCode({ code, type, id }) {
    return this.request('/payments/promo/validate', {
      method: 'POST',
      body: { code, type, id },
    });
  }

  createPaymentSession(assessmentId, options = {}) {
    return this.request(`/assessments/${assessmentId}/payment/session`, {
      method: 'POST',
      body: options,
    });
  }

  verifyPayment(assessmentId, { reference, outcome }) {
    return this.request(`/assessments/${assessmentId}/payment/verify`, {
      method: 'POST',
      body: { reference, outcome },
    });
  }

  confirmAssessment(assessmentId, { preference, signature }) {
    return this.request(`/assessments/${assessmentId}/confirm`, {
      method: 'POST',
      body: { preference, signature },
    });
  }

  /** Ticket 8089: the patient's preferred pathway — not an acceptance. */
  saveAssessmentPreference(assessmentId, preference) {
    return this.request(`/assessments/${assessmentId}/preference`, {
      method: 'POST',
      body: { preference },
    });
  }

  // ---- Provisional plans (issue 8216) ----
  /** The plan the Assessment Report renders — released, else library default. */
  getProvisionalPlan(patientId) {
    return this.request(`/provisional-plans/latest${patientQuery(patientId)}`);
  }

  /** 8591 — the patient accepts their Final Treatment Plan; 8723 — the approved option they chose. */
  acceptFinalPlan(patientId, optionKey) {
    return this.request(`/provisional-plans/accept-final${patientQuery(patientId)}`, {
      method: 'POST',
      ...(optionKey ? { body: { optionKey } } : {}),
    });
  }

  // ---- Realignment 3.5: treatment consent before accepting the final plan ----
  /** The consent document (text + sha256) for one option of the patient's released final plan. */
  getTreatmentConsentDocument(patientId, optionKey) {
    const q = new URLSearchParams();
    if (patientId) q.set('patientId', patientId);
    if (optionKey) q.set('optionKey', optionKey);
    const qs = q.toString();
    return this.request(`/treatment-consent/document${qs ? `?${qs}` : ''}`);
  }

  /** Sign it: { optionKey, documentHash, signerName, signerRole, signerRelationship, agreed, signature? } */
  signTreatmentConsent(patientId, payload) {
    return this.request(`/treatment-consent/sign${patientQuery(patientId)}`, { method: 'POST', body: payload });
  }

  /** Clinician: consents signed on a plan, read-only. */
  getPlanConsents(planId) {
    return this.request(`/treatment-consent/plan/${encodeURIComponent(planId)}`);
  }

  /** Clinician: the stored consent snapshot as a text file. */
  downloadTreatmentConsent(consentId) {
    return this.requestBlob(`/treatment-consent/${encodeURIComponent(consentId)}/download`);
  }

  treatmentConsentSignature(consentId) {
    return this.requestBlob(`/treatment-consent/${encodeURIComponent(consentId)}/signature`);
  }

  /** Clinician or the patient (their own): the signed consent as a PDF. */
  downloadTreatmentConsentPdf(consentId) {
    return this.requestBlob(`/treatment-consent/${encodeURIComponent(consentId)}/download.pdf`);
  }

  // ---- Realignment L6 follow-up: the data-use permission wording (admin) ----
  listDataUseWordings() {
    return this.request('/data-use-consent-wordings');
  }

  /** { title, body, checkboxLabel } → a new draft version. */
  createDataUseWordingDraft(wording) {
    return this.request('/data-use-consent-wordings', { method: 'POST', body: wording });
  }

  approveDataUseWording(version) {
    return this.request(`/data-use-consent-wordings/${encodeURIComponent(version)}/approve`, { method: 'POST', body: {} });
  }

  /** Admin: the clinic's treatment consent clauses (versioned). */
  getConsentTemplate() {
    return this.request('/treatment-consent/template');
  }

  saveConsentTemplate(payload) {
    return this.request('/treatment-consent/template', { method: 'PUT', body: payload });
  }

  /** 8591: checkout for the Final Treatment Plan the patient accepted. */
  createTreatmentPaymentSession(planId, options = {}) {
    return this.request(`/provisional-plans/final-payment/${planId}/session`, { method: 'POST', body: options });
  }

  verifyTreatmentPayment(planId, { reference, outcome }) {
    return this.request(`/provisional-plans/final-payment/${planId}/verify`, {
      method: 'POST',
      body: { reference, outcome },
    });
  }

  /**
   * Issue 8388: upload one Patient Education asset for a treatment package.
   * Returns `{ key, url, filename, mimeType, size }`; the url is what the
   * education resource stores.
   */
  async uploadPackageAsset(file) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE_URL}/treatment-packages/assets`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return payload?.data?.asset;
  }

  /**
   * Ticket 8487: upload a file the clinician attaches to a treatment plan
   * option's education package. Same store and rules as package assets; the
   * route is open to clinicians rather than admins only.
   */
  async uploadTreatmentPlanAsset(file) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE_URL}/treatment-plan/education-assets`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return payload?.data?.asset;
  }

  // ---- Unified Treatment Plan (issue 8430) ----
  /** Open a patient's plan — created only if this episode has none. */
  openTreatmentPlan({ patient, patientName } = {}) {
    const q = new URLSearchParams();
    if (patient) q.set('patient', patient);
    if (patientName) q.set('patientName', patientName);
    const qs = q.toString();
    return this.request(`/treatment-plan/open${qs ? `?${qs}` : ''}`);
  }

  getTreatmentPlan(id, version) {
    const qs = version ? `?version=${encodeURIComponent(version)}` : '';
    return this.request(`/treatment-plan/${id}${qs}`);
  }

  saveTreatmentPlanContent(id, content) {
    return this.request(`/treatment-plan/${id}/content`, { method: 'PUT', body: content });
  }

  /** 8980: Auto-Save — the editor's unsaved work as the plan's working draft (not a version). */
  saveTreatmentPlanDraft(id, content) {
    return this.request(`/treatment-plan/${id}/draft`, { method: 'PUT', body: content });
  }

  /** 8980: Discard Changes — drop the working draft. */
  discardTreatmentPlanDraft(id) {
    return this.request(`/treatment-plan/${id}/draft`, { method: 'DELETE' });
  }

  /** 8980: the Publication Readiness Checklist for what is on screen. Saves nothing. */
  treatmentPlanReleaseReadiness(id, content) {
    return this.request(`/treatment-plan/${id}/release-readiness`, { method: 'POST', body: content || {} });
  }

  /** Realignment 1.3: what is missing from a patient's assessment, and asking them for it (staff). */
  getInformationCheck(profileId) {
    return this.request(`/onboarding/staff/${profileId}/information-check`);
  }

  draftClarificationRequest(profileId) {
    return this.request(`/onboarding/staff/${profileId}/clarification/draft`, { method: 'POST' });
  }

  sendClarificationRequest(profileId, body) {
    return this.request(`/onboarding/staff/${profileId}/clarification/send`, { method: 'POST', body });
  }

  /** Realignment 1.5: the Koral Assistant's approved knowledge and its learning queue (admin). */
  getAssistantKnowledge() {
    return this.request('/assistant/knowledge');
  }

  saveAssistantKnowledge(entry) {
    return entry.id
      ? this.request(`/assistant/knowledge/${entry.id}`, { method: 'PUT', body: entry })
      : this.request('/assistant/knowledge', { method: 'POST', body: entry });
  }

  decideAssistantKnowledge(id, action) {
    return this.request(`/assistant/knowledge/${id}/${action}`, { method: 'POST' });
  }

  getAssistantPairs(status = 'answered') {
    return this.request(`/assistant/pairs?status=${encodeURIComponent(status)}`);
  }

  promoteAssistantPair(id, entry) {
    return this.request(`/assistant/pairs/${id}/promote`, { method: 'POST', body: entry });
  }

  dismissAssistantPair(id) {
    return this.request(`/assistant/pairs/${id}/dismiss`, { method: 'POST' });
  }

  /** Realignment 2.3: draft the patient's summary of the current version, from the approved options. */
  draftTreatmentPlanSummary(id) {
    return this.request(`/treatment-plan/${id}/patient-summary/draft`, { method: 'POST' });
  }

  setTreatmentPlanStatus(id, status, detail) {
    return this.request(`/treatment-plan/${id}/status`, { method: 'PUT', body: { status, detail } });
  }

  /** 8640: publish one saved version as the Final Treatment Plan. */
  publishTreatmentPlanVersion(id, version) {
    return this.request(`/treatment-plan/${id}/versions/${version}/publish`, { method: 'POST' });
  }

  /** The Assessment Report bundle behind the plan (issue 8430). */
  /** 8475: package options for the worksheet's current findings (read-only). */
  previewTreatmentPlanOptions(id, findings) {
    return this.request(`/treatment-plan/${id}/options-preview`, { method: 'POST', body: { findings } });
  }

  getTreatmentPlanClinical(id, version) {
    const q = version ? `?version=${version}` : '';
    return this.request(`/treatment-plan/${id}/clinical${q}`);
  }

  getTreatmentPlanAudit(id) {
    return this.request(`/treatment-plan/${id}/audit`);
  }

  /** The patient's own chat with their care team (issue 8442 §7). */
  getMySmileReviewChat(patientId) {
    return this.request(`/smile-reviews/mine/chat${patientQuery(patientId)}`);
  }

  sendMySmileReviewChat(body, patientId) {
    return this.request(`/smile-reviews/mine/chat${patientQuery(patientId)}`, { method: 'POST', body: { body } });
  }

  /** Confirm one of the Koral Assistant's buttons (book, move, cancel). */
  runMySmileReviewChatAction(messageId, key, patientId) {
    return this.request(`/smile-reviews/mine/chat/actions${patientQuery(patientId)}`, { method: 'POST', body: { messageId, key } });
  }

  // ---- Clinical Smile Review workspace (issue 8441) ----
  #smileReviewQuery({ patient, userId, caseId, patientName, coversSubmittedAt, scanId } = {}) {
    const q = new URLSearchParams();
    if (patient) q.set('patient', patient);
    if (userId) q.set('userId', userId);
    if (caseId) q.set('caseId', caseId);
    if (patientName) q.set('patientName', patientName);
    if (coversSubmittedAt) q.set('coversSubmittedAt', coversSubmittedAt);
    if (scanId) q.set('scanId', scanId);
    const qs = q.toString();
    return qs ? `?${qs}` : '';
  }

  /** The clinician's single-patient workspace — no queue, no patient selector. */
  getSmileReviewWorkspace(query) {
    return this.request(`/smile-reviews/workspace${this.#smileReviewQuery(query)}`);
  }

  saveSmileReviewAssessment(id, patch) {
    return this.request(`/smile-reviews/assessments/${id}`, { method: 'PUT', body: patch });
  }

  runSmileReviewEngine(id) {
    return this.request(`/smile-reviews/assessments/${id}/engine`, { method: 'POST' });
  }

  /** Realignment 4.3 — the progress comparison of one scan (clinician only). */
  runScanComparison(scanId) {
    return this.request(`/monitoring/scans/${encodeURIComponent(scanId)}/comparison`, { method: 'POST' });
  }

  /** Realignment 4.3 — Accept / Reject / correct one view, or the overall reading. */
  recordComparisonVerdict(comparisonId, body) {
    return this.request(`/monitoring/comparisons/${encodeURIComponent(comparisonId)}/verdict`, { method: 'POST', body });
  }

  /** Approval confirms the clinical work; it does NOT reach the patient. */
  approveSmileReviewAssessment(id) {
    return this.request(`/smile-reviews/assessments/${id}/approve`, { method: 'POST' });
  }

  /** Publication is what makes the approved review visible to the patient. */
  publishSmileReviewAssessment(id) {
    return this.request(`/smile-reviews/assessments/${id}/publish`, { method: 'POST' });
  }

  /** 8672 — Save & Publish to Patient: the working copy, released in one action. */
  saveAndPublishSmileReviewAssessment(id, patch) {
    return this.request(`/smile-reviews/assessments/${id}/save-and-publish`, { method: 'POST', body: patch });
  }

  // ---- Tasks & Reviews on the Clinical Smile Review (8674) ----
  getClinicalTasks(query, scanId) {
    return this.request(`/clinical-tasks${this.#smileReviewQuery({ ...query, scanId })}`);
  }

  getClinicalTaskAssignees() {
    return this.request('/clinical-tasks/assignees');
  }

  createClinicalTask(query, body) {
    return this.request(`/clinical-tasks${this.#smileReviewQuery(query)}`, { method: 'POST', body });
  }

  resolveClinicalTask(id, notes) {
    return this.request(`/clinical-tasks/${id}/resolve`, { method: 'POST', body: { notes } });
  }

  dismissClinicalTask(id, notes) {
    return this.request(`/clinical-tasks/${id}/dismiss`, { method: 'POST', body: { notes } });
  }

  markAllClinicalTasksReviewed(query, scanId) {
    return this.request(`/clinical-tasks/mark-all-reviewed${this.#smileReviewQuery({ ...query, scanId })}`, {
      method: 'POST',
    });
  }

  sendSmileReviewChat(query, body) {
    return this.request(`/smile-reviews/workspace/chat${this.#smileReviewQuery(query)}`, {
      method: 'POST',
      body: { body },
    });
  }

  /**
   * The shared patient conversation behind the Patient Chat panel (8605).
   * One resource for every clinical page that shows the panel, so the panel
   * cannot behave differently depending on which page it is on.
   */
  getPatientChat(query) {
    return this.request(`/smile-reviews/workspace/chat${this.#smileReviewQuery(query)}`);
  }

  sendPatientChat(query, body) {
    return this.sendSmileReviewChat(query, body);
  }

  // ---- Patient Manager (issue 8446) ----
  /**
   * The patient-contextual workspace. The patient travels in the query string
   * on every call, including the writes, because Patient Manager has no notion
   * of a "current patient" on the server — the context is the request's.
   */
  #patientManagerQuery({ patient, caseId, patientName } = {}) {
    const q = new URLSearchParams();
    if (patient) q.set('patient', patient);
    if (caseId) q.set('caseId', caseId);
    if (patientName) q.set('patientName', patientName);
    const qs = q.toString();
    return qs ? `?${qs}` : '';
  }

  getPatientManager(query) {
    return this.request(`/patient-manager${this.#patientManagerQuery(query)}`);
  }

  savePatientManagerRecord(query, patch) {
    return this.request(`/patient-manager${this.#patientManagerQuery(query)}`, {
      method: 'PUT',
      body: patch,
    });
  }

  addPatientManagerVisit(query, visit) {
    return this.request(`/patient-manager/visits${this.#patientManagerQuery(query)}`, {
      method: 'POST',
      body: visit,
    });
  }

  deletePatientManagerVisit(query, visitId) {
    return this.request(
      `/patient-manager/visits/${encodeURIComponent(visitId)}${this.#patientManagerQuery(query)}`,
      { method: 'DELETE' }
    );
  }

  addPatientManagerNote(query, body) {
    return this.request(`/patient-manager/notes${this.#patientManagerQuery(query)}`, {
      method: 'POST',
      body: { body },
    });
  }

  setPatientManagerAttendance(query, appointmentId, status) {
    return this.request(
      `/patient-manager/appointments/${encodeURIComponent(appointmentId)}/attendance${this.#patientManagerQuery(query)}`,
      { method: 'PUT', body: { status } }
    );
  }

  /** 8916: the practice's times on one date, with what is already taken. */
  getPatientManagerTimes(query, { date, appointmentId } = {}) {
    const q = new URLSearchParams(this.#patientManagerQuery(query));
    q.set('date', date);
    if (appointmentId) q.set('appointmentId', appointmentId);
    return this.request(`/patient-manager/times?${q.toString()}`);
  }

  /** 8916: save the planned Next Visit (never a booking). */
  savePatientManagerNextVisit(query, body) {
    return this.request(`/patient-manager/next-visit${this.#patientManagerQuery(query)}`, { method: 'PUT', body });
  }

  /**
   * 8930: the Next Visit panel's Auto-Suggested recommendation —
   * `{ available, suggestion }` or `{ available: false, message }`.
   */
  getPatientManagerNextVisitSuggestion(query) {
    return this.request(`/patient-manager/next-visit-suggestion${this.#patientManagerQuery(query)}`);
  }

  /** 8916: move a confirmed appointment; `applyAsPlanned` re-anchors the recall. */
  reschedulePatientManagerAppointment(query, appointmentId, body) {
    return this.request(`/patient-manager/appointments/${appointmentId}/reschedule${this.#patientManagerQuery(query)}`, { method: 'POST', body });
  }

  /** 8916: Save Recall — the recurring configuration only. */
  savePatientManagerRecall(query, body) {
    return this.request(`/patient-manager/recall${this.#patientManagerQuery(query)}`, { method: 'PUT', body });
  }

  /** 8916: save one Appointment History entry (status and notes). */
  savePatientManagerHistoryEntry(query, appointmentId, body) {
    return this.request(`/patient-manager/appointments/${appointmentId}${this.#patientManagerQuery(query)}`, { method: 'PUT', body });
  }

  /** 8916: record a patient's reschedule request (phone, message or chat). */
  recordPatientRescheduleRequest(query, body) {
    return this.request(`/patient-manager/reschedule-requests${this.#patientManagerQuery(query)}`, { method: 'POST', body });
  }

  declinePatientRescheduleRequest(query, requestId, note) {
    return this.request(`/patient-manager/reschedule-requests/${requestId}/decline${this.#patientManagerQuery(query)}`, {
      method: 'POST',
      body: { note },
    });
  }


  // ---- Workbench Dashboard (issue 8427) ----
  getWorkbenchContext(practiceId) {
    const qs = practiceId ? `?practiceId=${encodeURIComponent(practiceId)}` : '';
    return this.request(`/workbench/context${qs}`);
  }

  /** The Clinical Workbench: active items plus the actionable summary. */
  getWorkbenchBoard(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    const qs = q.toString();
    return this.request(`/workbench/board${qs ? `?${qs}` : ''}`);
  }

  /** 8704/8706: a clinician names the workflow for a check-in that could not be routed. */
  resolveRoutingException(appointmentId, body) {
    return this.request(`/workbench/routing-exceptions/${appointmentId}/resolve`, { method: 'POST', body });
  }

  getWorkbenchSchedule(practiceId) {
    const qs = practiceId ? `?practiceId=${encodeURIComponent(practiceId)}` : '';
    return this.request(`/workbench/schedule${qs}`);
  }

  getWorkbenchAssessments() {
    return this.request('/workbench/assessments');
  }

  // ---- Admin Case Pipeline (adversarial-audit remediation, 2026-09-06) ----
  getCasePipeline() {
    return this.request('/case-pipeline');
  }

  searchWorkbenchPatients(search) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/workbench/patients${qs}`);
  }

  createWorkbenchActivity(payload) {
    return this.request('/workbench/items', { method: 'POST', body: payload });
  }

  setWorkbenchItemStatus(id, status) {
    return this.request(`/workbench/items/${id}/status`, { method: 'PUT', body: { status } });
  }

  // ---- ADA Practice Fee Management (issue 8415) ----
  getAdaFeeContext() {
    return this.request('/ada-fees/context');
  }

  listAdaFees(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    const qs = q.toString();
    return this.request(`/ada-fees${qs ? `?${qs}` : ''}`);
  }

  saveAdaFee(id, payload) {
    return this.request(`/ada-fees/${id}`, { method: 'PUT', body: payload });
  }

  resetAdaFee(id, practiceId) {
    return this.request(`/ada-fees/${id}/reset`, { method: 'POST', body: { practiceId } });
  }

  setAdaDefaultFee(id, defaultFee) {
    return this.request(`/ada-fees/${id}/default-fee`, { method: 'PUT', body: { defaultFee } });
  }

  /** 8448: ADA verification is Central Admin's, and separate from any fee. */
  setAdaVerification(id, requiresVerification) {
    return this.request(`/ada-fees/${id}/verification`, {
      method: 'PUT',
      body: { requiresVerification },
    });
  }

  getAdaFeeHistory(id, { practiceId, q } = {}) {
    const params = new URLSearchParams();
    if (practiceId) params.set('practiceId', practiceId);
    if (q) params.set('q', q);
    const qs = params.toString();
    return this.request(`/ada-fees/${id}/history${qs ? `?${qs}` : ''}`);
  }

  // ---- Account ----
  getAccountProfile() {
    return this.request('/account/profile');
  }

  saveAccountProfile(profile) {
    return this.request('/account/profile', { method: 'PUT', body: profile });
  }

  /** Register 8.13: finish an email change with the code sent to the new address. */
  confirmAccountEmailChange(code) {
    return this.request('/account/email/confirm', { method: 'POST', body: { code } });
  }

  cancelAccountEmailChange() {
    return this.request('/account/email/pending', { method: 'DELETE' });
  }

  // Ticket 8719: Account Management → Family Members. The same patient
  // profiles the onboarding wizard and the dashboard create.
  getFamilyMembers() {
    return this.request('/account/family-members');
  }

  addFamilyMember(member) {
    return this.request('/account/family-members', { method: 'POST', body: member });
  }

  updateFamilyMember(id, member) {
    return this.request(`/account/family-members/${id}`, { method: 'PUT', body: member });
  }

  removeFamilyMember(id) {
    return this.request(`/account/family-members/${id}`, { method: 'DELETE' });
  }

  // ---- Consultations ----
  createConsultation(payload) {
    return this.request('/consultations', { method: 'POST', body: payload });
  }

  cancelConsultation(id, payload = {}) {
    return this.request(`/consultations/${id}/cancel`, { method: 'POST', body: payload });
  }

  /**
   * Ticket 8489: give a held slot back without cancelling the consultation.
   * Sent with `keepalive` so a release fired as the patient leaves the payment
   * page still completes after the page is gone.
   */
  releaseConsultationHold(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    return fetch(`${API_BASE_URL}/consultations/${id}/release`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: '{}',
    })
      .then((res) => (res.ok ? res.json().then((p) => p?.data) : null))
      .catch(() => null);
  }

  /**
   * The consultation the checkout/scheduling pages act on. Scoped to the active
   * patient when one is given (ticket 8151); without one it is the account's
   * latest request.
   */
  getLatestConsultation(patientId) {
    return this.request(`/consultations/latest${patientQuery(patientId)}`);
  }

  createConsultationPaymentSession(consultationId, options = {}) {
    return this.request(`/consultations/${consultationId}/payment/session`, {
      method: 'POST',
      body: options,
    });
  }

  verifyConsultationPayment(consultationId, { reference, outcome }) {
    return this.request(`/consultations/${consultationId}/payment/verify`, {
      method: 'POST',
      body: { reference, outcome },
    });
  }

  getConsultationBranches() {
    return this.request('/consultations/branches');
  }

  /**
   * Available consultation slots. With a `branch` (and `date` = YYYY-MM-DD)
   * returns the Morning/Afternoon grouped slots for that branch+date; called
   * with no args it returns the legacy flat upcoming-slot list.
   */
  getConsultationSlots(branch, date) {
    const params = new URLSearchParams();
    if (branch) params.set('branch', branch);
    if (date) params.set('date', date);
    const qs = params.toString();
    return this.request(`/consultations/slots${qs ? `?${qs}` : ''}`);
  }

  joinWaitlist({ branch, preferredDate, preferredTime, flexibility, contactMethod } = {}) {
    return this.request('/consultations/waitlist', {
      method: 'POST',
      body: { branch, preferredDate, preferredTime, flexibility, contactMethod },
    });
  }

  /** The caller's live waiting-list requests (issue 8253). */
  getWaitlist() {
    return this.request('/consultations/waitlist');
  }

  /** Withdraw a waiting-list request (issue 8253). */
  cancelWaitlist(id) {
    return this.request(`/consultations/waitlist/${id}/cancel`, { method: 'POST' });
  }

  // ---- Clinical (staff) ----
  /**
   * Clinical Review Dashboard. Ticket 8453: the toolbar criteria and the page
   * are the server's to apply, so a filter narrows the whole queue rather than
   * the rows that happen to be on screen.
   */
  getClinicalReviewDashboard(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, v);
    });
    const qs = query.toString();
    return this.request(`/clinical/review-dashboard${qs ? `?${qs}` : ''}`);
  }

  getClinicalCase(caseId) {
    return this.request(`/clinical/cases/${caseId}`);
  }

  finalizeClinicalCase(caseId, { findings, notes, recommendation }) {
    return this.request(`/clinical/cases/${caseId}/finalize`, {
      method: 'POST',
      body: { findings, notes, recommendation },
    });
  }

  saveClinicalOverride(caseId, payload) {
    return this.request(`/clinical/cases/${caseId}/override`, { method: 'POST', body: payload });
  }

  getClinicalCorrections(caseId) {
    return this.request(`/clinical/cases/${caseId}/corrections`);
  }

  // ---- AI Learning Rules (admin) ----
  getLearningRules() {
    return this.request('/clinical/learning-rules');
  }

  createLearningRule(payload) {
    return this.request('/clinical/learning-rules', { method: 'POST', body: payload });
  }

  updateLearningRule(id, payload) {
    return this.request(`/clinical/learning-rules/${id}`, { method: 'PUT', body: payload });
  }

  deleteLearningRule(id) {
    return this.request(`/clinical/learning-rules/${id}`, { method: 'DELETE' });
  }

  // ---- AI Candidate Improvements (admin) ----
  getCandidateImprovements() {
    return this.request('/clinical/candidate-improvements');
  }

  getCandidateImprovementDetails(id) {
    return this.request(`/clinical/candidate-improvements/${id}/details`);
  }

  approveCandidateImprovement(id) {
    return this.request(`/clinical/candidate-improvements/${id}/approve`, { method: 'POST' });
  }

  getTreatmentDashboard() {
    return this.request('/clinical/treatment-dashboard');
  }

  getEncounter(caseId) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}`);
  }

  /**
   * One of a patient's photos or X-rays, for a clinician reviewing their
   * record (8430 / 8438). The Smile Review photo route serves submission
   * photos only, so an X-ray — which both prototypes show — 404s there.
   */
  async fetchClinicalMediaUrl(mediaId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/clinical/media/${mediaId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.ok) return { url: URL.createObjectURL(await res.blob()), reason: '' };
    // The server distinguishes "no such record" from "the file is gone from
    // this server", and a clinician looking at an empty frame needs to know
    // which one they are looking at.
    const reason = await res
      .json()
      .then((body) => body?.error?.message || '')
      .catch(() => '');
    return { url: null, reason: reason || 'This record could not be opened.' };
  }

  /** 8686 — First-Encounter Modality Correction (dentist only). */
  correctModality(caseId, { toTxType, reason }) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/modality-correction`, {
      method: 'POST',
      body: { toTxType, reason },
    });
  }

  /**
   * 8687 — the shared Images & Scans resource: one patient's media in the
   * standard categories, with source and replacement rules resolved server-side.
   */
  getImagesAndScans({ profileId, userId }) {
    const q = new URLSearchParams();
    if (profileId) q.set('profileId', profileId);
    if (userId) q.set('userId', userId);
    return this.request(`/clinical/images?${q.toString()}`);
  }

  async _clinicalImageForm(path, fields, file) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => v != null && v !== '' && form.append(k, v));
    form.append('file', file);
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(payload?.error?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return payload?.data?.item;
  }

  /**
   * 8687 — a clinician upload (Clinical Management Mode only). 8920: filed
   * under an Image Set (`imageSet`) or a named Custom Set (`customSet`); it is
   * always a new record and never replaces one already on file.
   */
  uploadClinicalImage({ profileId, userId, category, view, contextRef, note, xrayType, imageSet, customSet, file }) {
    return this._clinicalImageForm(
      '/clinical/images',
      { profileId, userId, category, view, contextRef, note, xrayType, imageSet, customSet },
      file
    );
  }

  // ---- Radiographs (Realignment 3.1 / 3.2) ----
  /** A patient's X-rays with their types, latest OPG reading and latest ceph analysis. */
  getRadiographs({ profileId, userId }) {
    const q = new URLSearchParams();
    if (profileId) q.set('profileId', profileId);
    if (userId) q.set('userId', userId);
    return this.request(`/clinical/radiographs?${q.toString()}`);
  }

  /** Set or correct an X-ray's type: opg | lateral_ceph | other | '' (not set). */
  setXrayType(mediaId, xrayType) {
    return this.request(`/clinical/radiographs/${encodeURIComponent(mediaId)}/type`, { method: 'PUT', body: { xrayType } });
  }

  /** Read an OPG; with `planId` its findings join that plan as proposals. */
  readOpg(mediaId, { planId } = {}) {
    return this.request(`/clinical/radiographs/${encodeURIComponent(mediaId)}/opg-reading`, { method: 'POST', body: { planId } });
  }

  /** Start a cephalometric analysis: mode 'ai' (proposed landmarks) or 'manual'. */
  startCephAnalysis(mediaId, mode = 'ai') {
    return this.request(`/clinical/radiographs/${encodeURIComponent(mediaId)}/ceph`, { method: 'POST', body: { mode } });
  }

  getCephAnalysis(id) {
    return this.request(`/clinical/radiographs/ceph/${encodeURIComponent(id)}`);
  }

  /** Save the clinician's landmarks; `confirm` turns the draft into the record. */
  saveCephAnalysis(id, { landmarks, confirm = false }) {
    return this.request(`/clinical/radiographs/ceph/${encodeURIComponent(id)}`, { method: 'PUT', body: { landmarks, confirm } });
  }

  discardCephAnalysis(id) {
    return this.request(`/clinical/radiographs/ceph/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** 8687 — replace a clinician upload; the replaced version is kept. */
  replaceClinicalImage(mediaId, { contextRef, note, file }) {
    return this._clinicalImageForm(`/clinical/images/${encodeURIComponent(mediaId)}/replace`, { contextRef, note }, file);
  }

  /** 8687 — the selected Category or Session as a ZIP, saved by the browser. */
  downloadImagesZip({ profileId, userId, group }) {
    return this._saveZip('/clinical/images/download', { group, profileId, userId });
  }

  /**
   * 8920 — Manage Export: the chosen Image Sets of one source (`patient` or
   * `clinician`) as a ZIP, one dated folder per set, saved by the browser.
   */
  exportImageSets({ profileId, userId, source, sets, category }) {
    return this._saveZip('/clinical/images/export', {
      source,
      sets: (sets || []).join(','),
      category: category && category !== 'all' ? category : '',
      profileId,
      userId,
    });
  }

  async _saveZip(path, params) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && q.set(k, v));
    const res = await fetch(`${API_BASE_URL}${path}?${q.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error?.message || `Download failed (${res.status})`);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || 'images.zip';
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return filename;
  }

  /**
   * The read-only clinical context the Chair-side Workspace opens with (8438):
   * the patient and their alerts, the treatment case, their images and their
   * visit history — assembled server-side from the tables that own each part.
   */
  getEncounterContext(caseId, patient = '') {
    // The patient the link named travels with the reference, so a reference
    // that belongs to someone else is refused rather than opened.
    const q = patient ? `?patient=${encodeURIComponent(patient)}` : '';
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/context${q}`);
  }

  /** 8688 — one section's own Save: 'notes' or 'documentation'. */
  saveEncounterSection(caseId, section, payload) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/sections/${section}`, {
      method: 'PATCH',
      body: payload,
    });
  }

  saveEncounter(caseId, data) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}`, {
      method: 'PUT',
      body: { data },
    });
  }

  /** Realignment 3.3 — the encounter's consultation summary: read, draft, edit, approve, send. */
  getConsultationSummary(caseId) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/consultation-summary`);
  }

  draftConsultationSummary(caseId) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/consultation-summary/draft`, { method: 'POST' });
  }

  saveConsultationSummary(caseId, text) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/consultation-summary`, {
      method: 'PUT',
      body: { text },
    });
  }

  approveConsultationSummary(caseId) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/consultation-summary/approve`, { method: 'POST' });
  }

  sendConsultationSummary(caseId) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/consultation-summary/send`, { method: 'POST' });
  }

  completeEncounter(caseId, { data, readyForRelease }) {
    return this.request(`/clinical/encounters/${encodeURIComponent(caseId)}/complete`, {
      method: 'POST',
      body: { data, readyForRelease },
    });
  }

  rejectCandidateImprovement(id) {
    return this.request(`/clinical/candidate-improvements/${id}/reject`, { method: 'POST' });
  }

  snoozeCandidateImprovement(id) {
    return this.request(`/clinical/candidate-improvements/${id}/snooze`, { method: 'POST' });
  }

  // ---- Appointments (dentist) ----
  getAppointments() {
    return this.request('/appointments');
  }

  getAppointmentWorkspace(appointmentId) {
    return this.request(`/appointments/${appointmentId}/workspace`);
  }

  // ---- Appointments (receptionist scheduling) ----
  getAppointmentBoard({
    status,
    carePathway,
    search,
    branch,
    // Issue 8398: the date the receptionist is working in, or the week the
    // calendar view is drawing, plus the operational filters.
    date,
    from,
    to,
    providerId,
    appointmentType,
    origin,
  } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (carePathway) params.set('carePathway', carePathway);
    if (search) params.set('search', search);
    // Issue 8221: scope the board to one practice.
    if (branch) params.set('branch', branch);
    if (date) params.set('date', date);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (providerId) params.set('providerId', providerId);
    if (appointmentType) params.set('appointmentType', appointmentType);
    if (origin) params.set('origin', origin);
    const qs = params.toString();
    return this.request(`/appointments/board${qs ? `?${qs}` : ''}`);
  }

  getAppointmentProviders() {
    return this.request('/appointments/providers');
  }

  searchAppointmentPatients(search) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/appointments/patients${qs}`);
  }

  createAppointment(payload) {
    return this.request('/appointments', { method: 'POST', body: payload });
  }

  updateAppointment(id, payload) {
    return this.request(`/appointments/${id}`, { method: 'PUT', body: payload });
  }

  getAppointmentDetail(id) {
    return this.request(`/appointments/${id}`);
  }

  /** Issue 8398 §4: the statuses this appointment may move to now (server's rule). */
  getAppointmentTransitions(id) {
    return this.request(`/appointments/${id}/transitions`);
  }

  recordAppointmentFollowup(id, payload) {
    return this.request(`/appointments/${id}/followups`, { method: 'POST', body: payload });
  }

  scheduleConsultation(consultationId, { slot, branch }) {
    return this.request(`/consultations/${consultationId}/schedule`, {
      method: 'POST',
      body: { slot, branch },
    });
  }

  /**
   * The published assessment and consultation fees, as the backend prices them
   * (payments/pricing.js). Pages quote fees from here and keep no figure of
   * their own. Also says whether payments here are test payments.
   */
  getPublishedFees() {
    return this.request('/payments/fees');
  }

  // ---- Multi-factor authentication (register 8.14) ----
  /** Finish an MFA-gated sign-in with one of the account's one-time recovery codes. */
  verifyMfaRecoveryCode({ mfaToken, recoveryCode }) {
    return this.request('/auth/mfa/verify', { method: 'POST', body: { mfaToken, recoveryCode } });
  }

  /** Whether the signed-in account has an authenticator app set up. Never the key. */
  getMfaStatus() {
    return this.request('/auth/mfa/status');
  }

  /** Begin setting up (or replacing) an authenticator app; needs the current password. */
  startMfaEnrolment({ currentPassword }) {
    return this.request('/auth/mfa/enrol', { method: 'POST', body: { currentPassword } });
  }

  /** Confirm it with a first code from the new app. Returns the recovery codes, once. */
  confirmMfaEnrolment({ mfaToken, code }) {
    return this.request('/auth/mfa/enrol/confirm', { method: 'POST', body: { mfaToken, code } });
  }

  /** Admin: whether an account has an authenticator app set up. */
  getUserMfaStatus(id) {
    return this.request(`/users/${id}/mfa`);
  }

  /** Admin: reset an account's MFA so it is set up again at the next sign-in. */
  resetUserMfa(id) {
    return this.request(`/users/${id}/reset-mfa`, { method: 'POST', body: {} });
  }

  /** A staff-only note on a conversation: stored on the thread, never shown to the patient. */
  addConversationInternalNote(id, body) {
    return this.request(`/conversations/${id}/messages`, { method: 'POST', body: { body, internal: true } });
  }

  // ---- X-ray request (register 2.11) ----

  /** Patient: whether a clinician has issued an X-ray request for this patient. */
  getXrayReferralStatus({ patientId } = {}) {
    const qs = patientId ? `?patientId=${encodeURIComponent(patientId)}` : '';
    return this.request(`/consultations/xray-referral/status${qs}`);
  }

  /** Staff: the examinations that can be requested and what is on file for the patient. */
  getXrayReferrals({ profileId, userId } = {}) {
    const params = new URLSearchParams();
    if (profileId) params.set('patient', profileId);
    if (userId) params.set('userId', userId);
    return this.request(`/consultations/xray-referrals?${params.toString()}`);
  }

  /** Staff: issue an X-ray request — the ticked examinations and the clinical indication. */
  issueXrayReferral({ profileId, userId }, { examinations, indication }) {
    return this.request('/consultations/xray-referrals', {
      method: 'POST',
      body: { patient: profileId || undefined, userId: userId || undefined, examinations, indication },
    });
  }

  /** Staff: withdraw the patient's X-ray request. */
  withdrawXrayReferral({ profileId, userId }, id) {
    return this.request(`/consultations/xray-referrals/${id}/withdraw`, {
      method: 'POST',
      body: { patient: profileId || undefined, userId: userId || undefined },
    });
  }

  // ---- Fees and cancellation policy (Admin) ----

  getClinicFees() {
    return this.request('/clinic-settings/fees');
  }

  updateClinicFees(fees) {
    return this.request('/clinic-settings/fees', { method: 'PUT', body: fees });
  }

  // ---- Clinical rules sign-off (Admin or dentist) ----

  /** The case rules version in the code, and whether it has been signed off. */
  getClinicalRulesStatus() {
    return this.request('/clinic-settings/clinical-rules');
  }

  /** Sign off the current rules version as the signed-in person. */
  signOffClinicalRules(note) {
    return this.request('/clinic-settings/clinical-rules/sign-off', { method: 'POST', body: { note: note || null } });
  }
  /** Realignment 2.4: whether the prices plans quote are signed off, and signing them off. */
  getPricingSignoff() {
    return this.request('/clinic-settings/pricing');
  }

  signOffPricing(note, expectedHash) {
    return this.request('/clinic-settings/pricing/sign-off', { method: 'POST', body: { note: note || null, expectedHash } });
  }

}

export default new ApiService();
